import { GridOptions } from "ag-grid-community";
import { ColDef } from "ag-grid-community/dist/lib/entities/colDef";
import { AgGridReact } from "ag-grid-react";
import React, { memo, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import {
  IgIconButton,
  IgTableWrapper,
  IgTooltip,
  IgTypography,
  IgTypographyTooltip,
} from "@intelligems/ig-theme";
import { ExperienceDto, HistoryDto } from "@intelligems/ig-types";
import { getExperimentType } from "@intelligems/ig-utils";
import { Grid } from "@mui/material";

import { useConfiguration } from "../../../../../../api/configurations/use-configuration";
import { useEndExperience } from "../../../../../../api/experience-actions/end-experience";
import { usePauseExperience } from "../../../../../../api/experience-actions/pause-experience";
import { useStartExperience } from "../../../../../../api/experience-actions/start-experience";
import { useGetExperiencesList } from "../../../../../../api/experiences-list/get-experiences-list";
import { noDecimalNumberFormatter } from "../../../../../../api/formatters/dashboard";
import { useGetOrg } from "../../../../../../api/organizations/get-organization";
import { useGetScriptStatus } from "../../../../../../api/shops/get-script-status";
import { useIsAdmin } from "../../../../../../api/user-auths/is-admin";
import { useLoggedInUser } from "../../../../../../api/user-auths/login";
import { AnalyticStatusChip } from "../../../../../../components/chips/analyticsStatusChip";
import { DateRender } from "../../../../../../components/DateRender";
import { AdminOverrideStartExpDialog } from "../../../../../../components/Dialogs/AdminOverrideStartExpDialog";
import { BillingStatusDialog } from "../../../../../../components/Dialogs/BillingStatusDialog";
import { ConfirmExperienceDeleteDialog } from "../../../../../../components/Dialogs/ConfirmExperienceDeleteDialog";
import { ConfirmIgChecklist } from "../../../../../../components/Dialogs/ConfirmIgChecklist";
import { EndShippingTestDialog } from "../../../../../../components/Dialogs/EndShippingTestDialog";
import { ExperienceTestInfo } from "../../../../../../components/Dialogs/ExperienceTestInfo";
import { PreviewExperience } from "../../../../../../components/Dialogs/PreviewExperience";
import { PreviewVariationCode } from "../../../../../../components/Dialogs/PreviewVariationCode";
import { TemplateTestGateDialog } from "../../../../../../components/Dialogs/TemplateTestGateDialog";
import { UserInfoPopup } from "../../../../../../components/Dialogs/UserInfoPopup";
import { checkSubscriptionStatus } from "../../../../../../components/ScopeWrappers/helpers";
import { AnalyticsIcon } from "../../../../../../images/Icons/ExperimentTable/AnalyticsIcon";
import { PreviewIcon } from "../../../../../../images/Icons/ExperimentTable/PreviewIcon";
import {
  DASHBOARD_ROUTES,
  EXPERIMENT_ROUTES,
} from "../../../../../../routes/routes";
import { selectedExperienceListFilters } from "../../../../../../store/slices/appSettings";
import { updateDashboardFilter } from "../../../../../../store/slices/dashboardFilters";
import {
  getDialog,
  isDialogOpen,
  setDialog,
} from "../../../../../../store/slices/global";
import { IAppState } from "../../../../../../store/types/store";
import {
  dateTimeConverter,
  fullDateTimeConverter,
} from "../../../../../../utils/dateConverters";
import { IdPopover } from "../../../../../../utils/IdPopover";
import {
  mpClickAnalyticsButton,
  mpClickExperienceTableName,
} from "../../../../../../utils/mixpanel";
import { sortByStatus } from "../../../../../../utils/sortings";
import DownloadPriceCsvDialog from "../../../../components/dialogs/DownloadPriceCsvDialog";
import { MarketPriceUpdaterDialog } from "../../../../components/dialogs/MarketPriceUpdaterDialog";
import { MarketUpdateConfirmDialog } from "../../../../components/dialogs/MarketUpdateConfirmDialog";
import { PriceUpdaterDialog } from "../../../../components/dialogs/PriceUpdaterDialog";
import { ShippingRatesDialog } from "../../../../components/dialogs/ShippingRatesDialog";
import { HaltExperienceButton } from "../../../../components/experienceTestActions/HaltExperienceButton";
import { StartExperienceButton } from "../../../../components/experienceTestActions/StartExperienceButton";
import { TestTypeIcon } from "../../../../components/TestTypeIcon";
import { MoreMenu } from "../MoreMenu";

import { StatusRenderer } from "./StatusRenderer";
import { getConfirmedIntegrationHistoryAction } from "./utils";

export const ExperiencesList = memo(function ExperiencesList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useLoggedInUser();
  const gridRef = useRef<AgGridReact>(null);
  const tableFilters = useSelector(selectedExperienceListFilters);

  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const experiencesList = useGetExperiencesList({ category: "experiment" });
  const organization = useGetOrg();
  const scriptStatus = useGetScriptStatus(organization?.data?.org.id);

  const isAdmin = useIsAdmin();
  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  )!.value;

  const isConfirmDialogOpen = useSelector(isDialogOpen("confirmIgChecklist"));
  const [currentExperience, setCurrentExperience] = useImmer<
    ExperienceDto | undefined
  >(undefined);

  const startExperience = useStartExperience();

  const endExperience = useEndExperience(false, currentExperience?.id);

  const pauseExperience = usePauseExperience();

  const isSubscriptionActive = checkSubscriptionStatus(true);

  const marketPriceUpdater = useSelector(getDialog("marketPriceUpdater"));
  const marketUpdateConfirm = useSelector(getDialog("marketUpdateConfirm"));

  const onAdminOverrideClick = (experience: ExperienceDto) => {
    currentExperimentSet(experience);
    if (
      experience.type === "content/template" &&
      scriptStatus.isSuccess &&
      (scriptStatus.data.type === "appEmbedFound" ||
        scriptStatus.data.type === "inlineFound" ||
        scriptStatus.data.type === "inlineAsyncFound" ||
        scriptStatus.data.type === "multipleScriptsWithAppEmbedFound" ||
        scriptStatus.data.type === "multipleScriptsFound" ||
        scriptStatus.data.type === "missing" ||
        scriptStatus.data.type === "incorrectScriptFound") &&
      !scriptStatus.data.hasTemplateScript
    ) {
      dispatch(setDialog({ dialog: "templateSnippetGate", open: true }));
    } else {
      dispatch(
        setDialog({
          dialog: "adminOverrideStartExp",
          open: true,
        })
      );
    }
  };

  const filteredRowData = useMemo(() => {
    if (experiencesList.isSuccess) {
      let filteredData = experiencesList.data.experiencesList;

      if (tableFilters) {
        if (tableFilters.status.length > 0) {
          filteredData = experiencesList.data.experiencesList.filter(
            (experiment) => tableFilters.status.includes(experiment.status!)
          );
        }
        if (tableFilters.search && tableFilters.search.length > 0) {
          filteredData = filteredData.filter(
            (experiment) =>
              experiment.name
                .toLowerCase()
                .includes(tableFilters.search!.toLowerCase()) ||
              experiment.id === tableFilters.search
          );
        }
        if (tableFilters.testTypes.length > 0) {
          filteredData = filteredData.filter((experiment) =>
            tableFilters.testTypes.includes(
              getExperimentType(experiment as ExperienceDto)
            )
          );
        }
      }
      return sortByStatus(filteredData);
    }
    return [];
  }, [
    experiencesList,
    experiencesList?.data?.experiencesList,
    tableFilters,
    currentExperience,
  ]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 100,
      resizable: false,
      menuTabs: [],
      suppressMovable: true,
    };
  }, []);

  useEffect(() => {
    gridRef.current?.api?.redrawRows();
  }, [filteredRowData]);

  const gridOptions = useMemo<GridOptions>(() => {
    return {
      getRowId: (params) => params.data.id,
    };
  }, []);

  const generateEditPath = (experience: ExperienceDto) => {
    let base;
    if (experience.type === "pricing") {
      base = EXPERIMENT_ROUTES.PriceTest;
    } else if (experience.type === "shipping") {
      base = EXPERIMENT_ROUTES.ShippingTest;
    } else if (experience.type === "personalization/offer") {
      base = EXPERIMENT_ROUTES.OfferTest;
    } else {
      base = EXPERIMENT_ROUTES.ContentTest;
    }
    return `${base}/${experience.id!}?action=edit${
      experience.type.includes("content") ? `&type=${experience.type}` : ""
    }`;
  };
  const priceUpdaterDispatch = (action: string) => {
    dispatch(
      setDialog({
        dialog: "priceUpdater",
        open: true,
        props: { action: action },
      })
    );
  };

  const marketPriceUpdaterDispatch = (action: string) => {
    dispatch(
      setDialog({
        dialog: "marketPriceUpdater",
        open: true,
        props: { action: action },
      })
    );
  };

  const marketOrPriceUpdaterDispatch = (
    action: string,
    experience: ExperienceDto
  ) => {
    if (
      experience.currency?.code &&
      storeCurrencyConfig?.value?.code !== experience.currency?.code
    )
      marketPriceUpdaterDispatch(action);
    else priceUpdaterDispatch(action);
  };

  const onExperimentEnd = (experience: ExperienceDto) => {
    currentExperimentSet(experience);
    if (experience.testTypes.hasTestShipping) {
      dispatch(setDialog({ dialog: "endShippingTest", open: true }));
    } else if (experience.testTypes.hasTestPricing) {
      marketOrPriceUpdaterDispatch("end", experience);
    } else {
      // Content / Campaign Test
      endExperience.mutate({
        body: { variationId: undefined },
        queryParams: undefined,
        pathParams: {
          name: experience.name!,
          experienceId: experience.id!,
          action: "end",
        },
      });
    }
  };

  const currentExperimentSet = (experience: ExperienceDto) => {
    setCurrentExperience(experience);
  };

  const onExperimentStart = (experience: ExperienceDto) => {
    currentExperimentSet(experience);
    if (experience.testTypes.hasTestPricing) {
      if (!shouldDuplicateProducts) {
        marketOrPriceUpdaterDispatch("start", experience);
      } else {
        navigate(`${EXPERIMENT_ROUTES.DuplicateProducts}/${experience.id}`, {
          state: { action: "start" },
        });
      }
    } else {
      // Template Test snippet check
      if (
        experience.type === "content/template" &&
        scriptStatus.isSuccess &&
        (scriptStatus.data.type === "appEmbedFound" ||
          scriptStatus.data.type === "inlineFound" ||
          scriptStatus.data.type === "inlineAsyncFound" ||
          scriptStatus.data.type === "multipleScriptsWithAppEmbedFound" ||
          scriptStatus.data.type === "multipleScriptsFound" ||
          scriptStatus.data.type === "missing" ||
          scriptStatus.data.type === "incorrectScriptFound") &&
        !scriptStatus.data.hasTemplateScript
      ) {
        dispatch(setDialog({ dialog: "templateSnippetGate", open: true }));
      } else {
        // Content/Campaign Test/Shipping
        startExperience.mutate({
          body: { variationId: undefined },
          pathParams: {
            pausedAtTs: experience.pausedAtTs,
            experienceId: experience.id!,
            name: experience.name,
            action: "start",
          },
          queryParams: undefined,
        });
      }
    }
  };

  const onStartButtonClick = (
    rowExperience: ExperienceDto,
    history: HistoryDto[],
    isSubscriptionStartActive?: boolean
  ) => {
    setCurrentExperience(rowExperience);
    const isAdminOverride =
      isAdmin &&
      !isSubscriptionStartActive &&
      (rowExperience.status === "paused" || rowExperience.status === "pending");
    if (!getConfirmedIntegrationHistoryAction(history)) {
      dispatch(
        setDialog({
          dialog: "confirmIgChecklist",
          open: true,
          props: { isAdminOverride },
        })
      );
    } else if (isAdminOverride) {
      onAdminOverrideClick(rowExperience);
    } else {
      onExperimentStart(rowExperience);
    }
  };

  const onExperimentPause = (experience: ExperienceDto) => {
    currentExperimentSet(experience);
    if (experience.testTypes.hasTestPricing) {
      // Price / Combo Price Shipping Pause
      marketOrPriceUpdaterDispatch("pause", experience);
    } else {
      // Shipping / Content / Campaign Test
      pauseExperience.mutate({
        body: { variationId: undefined },
        queryParams: undefined,
        pathParams: {
          name: experience.name!,
          experienceId: experience.id!,
          action: "pause",
        },
      });
    }
  };

  const columnDefs: ColDef[] = useMemo(() => {
    if (experiencesList.isSuccess && filteredRowData) {
      return [
        {
          headerName: "EXPERIMENT NAME",
          headerClass: "header-custom",
          lockPosition: "left",
          field: "name",
          flex: 1,
          cellStyle: {
            justifyContent: "flex-start",
          },
          cellRenderer: (item) => {
            const isEdit =
              item.data.status === "pending" || item.data.status === "paused";
            const path = isEdit
              ? generateEditPath(item.data)
              : `${DASHBOARD_ROUTES.Dashboard_Overview}?experienceId=${item.data.id}`;
            const state = isEdit ? { id: item.data.id } : { name: "overview" };
            return (
              <IdPopover id={item.data.id}>
                <Link
                  style={{ textDecoration: "none", color: "black" }}
                  to={path}
                  state={state}
                  onClick={() => {
                    if (!isEdit) {
                      dispatch(
                        updateDashboardFilter({
                          key: "experienceId",
                          value: undefined,
                        })
                      );
                    }

                    mpClickExperienceTableName(isEdit ? "edit" : "analytics");
                  }}
                >
                  <Grid item container direction={"row"} alignItems={"center"}>
                    <Grid item xs={1.5}>
                      <TestTypeIcon testFlowType={item.data.type} />
                    </Grid>
                    <Grid item xs={10.5}>
                      <IgTypographyTooltip
                        textWidth={"250px"}
                        text={item.value}
                      />
                    </Grid>
                  </Grid>
                </Link>
              </IdPopover>
            );
          },
        },
        {
          headerName: "ORDERS/VISITORS",
          resizable: false,
          field: "orders",
          headerClass: "header-custom2",
          flex: 0.5,
          cellStyle: {
            justifyContent: "center",
          },
          cellRenderer: (item) => {
            if (item.data.status === "pending") {
              return <IgTypography>-------</IgTypography>;
            } else {
              return (
                <IgTypography
                  sx={{
                    fontSize: "15px",
                  }}
                >
                  {noDecimalNumberFormatter(
                    item.data.experienceAnalysis.orders,
                    false,
                    storeCurrencyConfig!.value
                  )}{" "}
                  /{" "}
                  {noDecimalNumberFormatter(
                    item.data.experienceAnalysis.visitors,
                    false,
                    storeCurrencyConfig!.value
                  )}
                </IgTypography>
              );
            }
          },
        },
        {
          headerName: "START",
          resizable: false,
          field: "startedAtTs",
          headerClass: "header-custom2",
          flex: 0.4,
          cellStyle: {
            justifyContent: "center",
          },
          cellRenderer: (item) => {
            return item.data.status === "pending" ? (
              <StartExperienceButton
                rowExperience={item.data}
                currentExperience={currentExperience}
                experiencesList={filteredRowData}
                organization={organization.data.org}
                onStartButtonClick={onStartButtonClick}
              />
            ) : (
              <IgTooltip title={fullDateTimeConverter(item.value)}>
                <DateRender date={dateTimeConverter(item.value)} />
              </IgTooltip>
            );
          },
        },
        {
          headerName: "END",
          field: "endedAtTs",
          resizable: false,
          flex: 0.4,
          headerClass: "header-custom2",
          cellStyle: {
            justifyContent: "center",
          },
          cellRenderer: (item) => {
            if (
              item.data.status === "ended" ||
              item.data.status === "pending"
            ) {
              return (
                <IgTooltip title={fullDateTimeConverter(item.value)}>
                  <DateRender date={dateTimeConverter(item.value)} />
                </IgTooltip>
              );
            } else {
              return (
                <Grid
                  item
                  container
                  direction={"row"}
                  justifyContent={"center"}
                >
                  <Grid item xs={6}>
                    <HaltExperienceButton
                      currentExperience={currentExperience}
                      onActionExpButtonClick={onExperimentEnd}
                      rowExperience={item.data}
                      action={"end"}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    {item.data.status === "paused" ? (
                      <StartExperienceButton
                        rowExperience={item.data}
                        currentExperience={currentExperience}
                        experiencesList={filteredRowData}
                        organization={organization.data.org}
                        onStartButtonClick={onStartButtonClick}
                      />
                    ) : (
                      <HaltExperienceButton
                        currentExperience={currentExperience}
                        onActionExpButtonClick={onExperimentPause}
                        rowExperience={item.data}
                        action={"pause"}
                      />
                    )}
                  </Grid>
                </Grid>
              );
            }
          },
        },
        {
          headerName: "STATUS",
          field: "status",
          flex: 0.4,
          resizable: false,
          headerClass: "header-custom2",
          cellStyle: {
            justifyContent: "center",
          },
          cellRenderer: (item) => {
            return <StatusRenderer experience={item.data} />;
          },
        },
        {
          headerName: "PROGRESS",
          field: "progress",
          resizable: false,
          headerClass: "header-custom2",
          flex: 0.45,
          cellStyle: {
            justifyContent: "center",
          },
          cellRenderer: (item) => {
            return <AnalyticStatusChip experience={item.data} />;
          },
        },
        {
          headerName: "",
          field: "analytics",
          headerClass: "header-custom2",
          resizable: false,
          flex: 0.1,
          cellRenderer: (item) => {
            if (item.data.status === "pending") {
              return (
                <IgTooltip title="Preview Experiment">
                  <IgIconButton
                    onClick={() => {
                      currentExperimentSet(item.data);
                      if (!scriptStatus.data?.exists) {
                        dispatch(
                          setDialog({
                            dialog: "redirectToPluginDialog",
                            open: true,
                            props: {
                              experience: item.data,
                              id: item.data.id,
                              path:
                                item.data.previewPath ||
                                item.data.devicePreview?.path,
                            },
                          })
                        );
                      } else {
                        dispatch(
                          setDialog({
                            dialog: "previewExperiment",
                            open: true,
                            props: {
                              id: item.data.id,
                              path:
                                item.data.previewPath ||
                                item.data.devicePreview?.path,
                            },
                          })
                        );
                      }
                    }}
                  >
                    <PreviewIcon sx={{ fontSize: "18px" }} />
                  </IgIconButton>
                </IgTooltip>
              );
            } else {
              return (
                <IgTooltip title="View Analytics">
                  <IgIconButton
                    onClick={() => {
                      if (!isSubscriptionActive && !isAdmin) return;
                      mpClickAnalyticsButton(
                        item.data.name,
                        item.data.id!,
                        "Analytics chart icon"
                      );
                      dispatch(
                        updateDashboardFilter({
                          key: "experienceId",
                          value: undefined,
                        })
                      );
                      navigate(
                        `${DASHBOARD_ROUTES.Dashboard_Overview}?experienceId=${item.data.id}`,
                        { state: { name: "overview" } }
                      );
                    }}
                    disabled={!isSubscriptionActive && !isAdmin}
                  >
                    <AnalyticsIcon sx={{ fontSize: "20px" }} />
                  </IgIconButton>
                </IgTooltip>
              );
            }
          },
        },
        {
          headerName: "",
          headerClass: "header-custom2",
          field: "more",
          resizable: false,
          flex: 0.1,
          cellRenderer: (item) => {
            return (
              <MoreMenu
                scriptStatus={scriptStatus.data || null}
                organization={organization.data!.org}
                experience={item.data}
                setCurrExperience={setCurrentExperience}
              />
            );
          },
        },
      ];
    } else return [];
  }, [
    experiencesList,
    filteredRowData,
    currentExperience,
    experiencesList.isSuccess,
    scriptStatus,
  ]);

  if (filteredRowData) {
    return (
      <Grid container direction={"column"} rowSpacing={3}>
        <UserInfoPopup />
        <TemplateTestGateDialog organization={organization.data.org} />
        {marketPriceUpdater.isOpen && currentExperience && (
          <MarketPriceUpdaterDialog currExperience={currentExperience} />
        )}
        {marketUpdateConfirm.isOpen && currentExperience && (
          <MarketUpdateConfirmDialog currExperience={currentExperience} />
        )}
        {currentExperience && (
          <>
            <ConfirmExperienceDeleteDialog
              currentExperience={currentExperience}
            />
            <ExperienceTestInfo experience={currentExperience} />
            <PriceUpdaterDialog currExperience={currentExperience} />
            <DownloadPriceCsvDialog currentExperience={currentExperience} />
            <ShippingRatesDialog currExperience={currentExperience} />
            <EndShippingTestDialog currExperience={currentExperience} />
            <AdminOverrideStartExpDialog
              experience={currentExperience}
              onExpButtonClick={onExperimentStart}
            />
            <PreviewExperience
              experience={currentExperience}
              organization={organization.data.org}
            />
            <PreviewVariationCode
              experience={currentExperience}
              variations={currentExperience.variations}
              organization={organization.data.org}
            />
            {isConfirmDialogOpen && (
              <ConfirmIgChecklist
                onExperienceStart={onExperimentStart}
                experience={currentExperience}
                onAdminOverrideClick={onAdminOverrideClick}
              />
            )}
          </>
        )}
        <BillingStatusDialog />
        <Grid item>
          <IgTableWrapper
            sx={{
              height: "468px",
              boxShadow: "0px 4px 8px rgba(140, 140, 140, 0.08);",
              border: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: "15px",
            }}
          >
            <AgGridReact
              ref={gridRef}
              suppressContextMenu={true}
              suppressCsvExport={true}
              columnDefs={columnDefs}
              rowData={filteredRowData}
              suppressAggFuncInHeader={true}
              suppressDragLeaveHidesColumns={true}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
              overlayLoadingTemplate={"<div>Loading Experiemnt Data ... </div>"}
            />
          </IgTableWrapper>
        </Grid>
      </Grid>
    );
  }
  return null;
});
